import {
  ElementPath,
  forEachElement,
  isElement,
  isProductElement,
  isDeletedElement,
  validatePath,
} from '@shared/helpers/recursive-element.helpers';
import {
  IProductListItem,
  IProductListGroup,
  IProductListCategoryGroup,
  OneOfProductListGroups,
} from '@shared/models/product.model';
import {
  ElementKind,
  Baseline,
  IProductElement,
  OneOfElements,
  OneOfParentElements,
} from '@shared/models/project.model';
import {
  ConversionFactorUnit,
  Results,
  ResultsRecord,
} from '@shared/models/unit.model';
import { required } from '@shared/helpers/function.helpers';
import { getElementCategoryId } from '@shared/helpers/element.helpers';
import { sumConversionFactors } from '@shared/helpers/conversion-factors.helpers';
import {
  isProductCategory,
  isServiceCategory,
} from '@shared/helpers/element-category.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { PartialRecord } from '@shared/models/type-helpers.model';
import { createRecordByKey, isDefined } from '@shared/helpers/array.helpers';
import { isElementActiveInProposal } from '@shared/helpers/proposal.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getElementSbefGroupKey,
  isSbefGroupFilterActive,
} from '@shared/helpers/sbef-results.helpers';
import { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';

import {
  getProductListElementId,
  PRODUCT_ID_SEPARATOR,
} from './product-list.helpers';

const ENERGY_UNIT: ConversionFactorUnit = 'kWh';

const energyCategoryIds = new Set<ElementCategoryID>([
  ElementCategoryID.Energy,
  ElementCategoryID.Heating,
  ElementCategoryID.Cooling,
  ElementCategoryID.AirHandlingUnit,
]);

const partitionEnergyGroups = (groups: IProductListGroup[]) => {
  return groups.reduce(
    (acc, group) => {
      if (group.unit === ENERGY_UNIT) acc.energyGroups.push(group);
      else acc.physicalEnergyGroups.push(group);
      return acc;
    },
    {
      energyGroups: [] as IProductListGroup[],
      physicalEnergyGroups: [] as IProductListGroup[],
    },
  );
};

const getOrCreateCategoryGroup = (
  categoryGroups: PartialRecord<ElementCategoryID, IProductListCategoryGroup>,
  categoryId: ElementCategoryID,
  unit: ConversionFactorUnit,
) => {
  if (!categoryGroups[categoryId]) {
    categoryGroups[categoryId] = {
      kind: ElementKind.ProductCategory,
      id: categoryId,
      unit,
      elements: [],
      results: {},
    };
  }
  return required(categoryGroups[categoryId]);
};

const mergeGroups = (
  existingGroups: IProductListGroup[],
  newGroups: IProductListGroup[],
) => {
  const existingRecord = createRecordByKey(existingGroups, 'id');
  newGroups.forEach((group) => {
    const existingGroup = existingRecord[group.id];
    if (existingGroup) {
      existingGroup.elements = [...existingGroup.elements, ...group.elements];
      existingGroup.results = sumConversionFactors(
        existingGroup.results,
        group.results,
      );
    } else {
      existingRecord[group.id] = group;
    }
  });
  return Object.values(existingRecord);
};

/**
 * SBEF scope applies to the nearest non-product element ancestor on the walk
 * from the list root. Product-category containers sit above SBEF-coded structure,
 * so the shallowest {@link isElement} node is often the category — not the SBEF
 * group owner.
 */
const getSbefScopeOwnerForProduct = (
  path: ElementPath,
  root: OneOfParentElements,
  subPath: ElementPath,
): OneOfElements | undefined =>
  [...path, root, ...subPath]
    .filter(isElement)
    .findLast((node) => !isProductElement(node));

/**
 * @param root
 * @param path If not a version path need to be provided to be accurate
 * @param resultsRecord Results from proposal.resultsRecord (element id -> results)
 */
export const createProductListGroups = (
  root: OneOfParentElements,
  path: ElementPath = [],
  groups: PartialRecord<IProductElement['id'], IProductListGroup> = {},
  resultsRecord: ResultsRecord = {},
  selectedSbefGroups: readonly SbefScopeGroupKey[] = [],
): IProductListGroup[] => {
  forEachElement(root.elements, (element, subPath) => {
    if (
      !isProductElement(element) ||
      !isElementActiveInProposal(undefined, subPath, element) ||
      subPath.find(isDeletedElement)
    ) {
      return;
    }

    // SBEF group filter: keep products whose nearest non-product element ancestor
    // on the walk from the list root belongs to a selected group.
    if (isSbefGroupFilterActive(selectedSbefGroups)) {
      const sbefOwner = getSbefScopeOwnerForProduct(path, root, subPath);
      const groupKey = sbefOwner && getElementSbefGroupKey(sbefOwner);
      if (sbefOwner && (!groupKey || !selectedSbefGroups.includes(groupKey))) {
        return;
      }
    }

    const { unit, generic_product_id, product_id } = element;
    const id = getProductListElementId(product_id, generic_product_id);

    if (!groups[id]) {
      groups[id] = {
        id,
        generic_product_id,
        unit,
        kind: ElementKind.ProductGroup,
        product_id: id.includes(PRODUCT_ID_SEPARATOR) ? product_id : undefined,
        results: {},
        elements: [],
      } as IProductListGroup;
    }
    const group = required(groups[id]);
    // Same reference seen twice when joining parent path segments — dedupe like lodash.uniq
    const fullPath = validatePath([...new Set([...path, root, ...subPath])]);
    group.elements.push(
      getProductListItem(element, fullPath, resultsRecord[element.id] ?? {}),
    );
  });

  return applyResults(Object.values(groups).filter(isDefined));
};

export const createProductListCategoryGroups = (
  version: Baseline,
  resultsRecord: ResultsRecord = {},
  selectedSbefGroups: readonly SbefScopeGroupKey[] = [],
): IProductListCategoryGroup[] => {
  const categoryGroups: PartialRecord<
    ElementCategoryID,
    IProductListCategoryGroup
  > = {};

  forEachElement(version, (element, path) => {
    const category_id = getElementCategoryId(element);
    const hasGeneratedChildren =
      isProductCategory(category_id) || isServiceCategory(category_id);

    if (
      isDeletedElement(element) ||
      !isElement(element) ||
      !isElementActiveInProposal(undefined, path, element) ||
      !hasGeneratedChildren
    ) {
      return;
    }

    const routeToEnergyBuckets = energyCategoryIds.has(category_id);

    let categoryGroup: IProductListCategoryGroup | undefined = undefined;
    if (!routeToEnergyBuckets) {
      if (!categoryGroups[category_id]) {
        categoryGroups[category_id] = {
          kind: ElementKind.ProductCategory,
          id: category_id,
          unit: element.selectedQuantity === 'energy' ? 'kWh' : 'kg',
          elements: [],
          results: {},
        };
      }
      categoryGroup = required(categoryGroups[category_id]);
    }

    const existingGroupsRecord = routeToEnergyBuckets
      ? {}
      : createRecordByKey(categoryGroup?.elements ?? [], 'id');
    const productGroups = createProductListGroups(
      element,
      path,
      existingGroupsRecord,
      resultsRecord,
      selectedSbefGroups,
    );

    if (routeToEnergyBuckets) {
      const { energyGroups, physicalEnergyGroups } =
        partitionEnergyGroups(productGroups);

      if (physicalEnergyGroups.length) {
        const otherProductsGroup = getOrCreateCategoryGroup(
          categoryGroups,
          ElementCategoryID.OtherProduct,
          'kg',
        );
        const merged = mergeGroups(
          otherProductsGroup.elements,
          physicalEnergyGroups,
        );
        otherProductsGroup.elements = merged;
      }

      if (energyGroups.length) {
        const energyCategoryGroup = getOrCreateCategoryGroup(
          categoryGroups,
          ElementCategoryID.Energy,
          'kWh',
        );
        const mergedEnergyGroups = mergeGroups(
          energyCategoryGroup.elements,
          energyGroups,
        );
        energyCategoryGroup.elements = mergedEnergyGroups;
      }

      return;
    }

    if (categoryGroup) {
      categoryGroup.elements = productGroups;
    }
  });

  return applyResults(Object.values(categoryGroups).filter(guards.is.defined));
};

/** Sum results of the child items in groups and apply it to the group. */
const applyResults = <T extends OneOfProductListGroups>(groups: T[]): T[] => {
  return groups.map((group) => ({
    ...group,
    results: sumConversionFactors(...group.elements.map((i) => i.results)),
  }));
};

const getProductListItem = (
  element: IProductElement,
  path: ElementPath,
  results: Results = {},
): IProductListItem => {
  return {
    ...element,
    path,
    kind: ElementKind.ProductItem,
    results,
  };
};

/**
 * All row ids selectable in the product list for a resolved version (groups,
 * categories, and product items). Used when validating URL selection after
 * proposal/baseline changes.
 */
export const collectProductListSelectableIds = (
  version: Baseline,
  resultsRecord: ResultsRecord = {},
): Set<string> => {
  const ids = new Set<string>();
  const addGroup = (group: IProductListGroup) => {
    ids.add(group.id);
    for (const item of group.elements) {
      ids.add(item.id);
    }
  };

  createProductListGroups(version, [], {}, resultsRecord).forEach(addGroup);
  createProductListCategoryGroups(version, resultsRecord).forEach(
    (category) => {
      ids.add(category.id);
      category.elements.forEach(addGroup);
    },
  );

  return ids;
};
